import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Shield, Lock, Eye, FileText, ChevronRight, ArrowLeft } from 'lucide-react';

const Privacy: React.FC = () => {
  const lastUpdated = '2026年3月31日';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-[#0b1220] dark:via-slate-900 dark:to-slate-800 py-8 sm:py-12 px-3 sm:px-4">
      <SEO 
        title="隐私政策"
        description="了解 lingdux.space 如何收集、使用和保护您的个人信息。"
      />
      <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8">
        
        {/* Header */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回首页
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-2xl bg-blue-100 dark:bg-blue-900/30">
              <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">隐私政策</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">最后更新：{lastUpdated}</p>
            </div>
          </div>
          
          <p className="text-slate-600 dark:text-slate-300 leading-7">
            本网站非常重视您的个人信息保护。本隐私政策说明了我们如何收集、使用、存储和保护您的信息。
          </p>
        </motion.section>

        {/* Content Sections */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-500" />
            信息收集
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>我们可能收集以下类型的信息：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>您主动提供的信息（如在评论或联系表单中填写的内容）</li>
              <li>浏览数据（包括访问时间、页面浏览量等，用于网站分析）</li>
              <li>设备信息（包括浏览器类型、操作系统等，用于优化访问体验）</li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-sky-500" />
            信息使用
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>我们收集的信息用于以下目的：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>提供和改进网站内容和服务</li>
              <li>分析网站访问量和使用情况</li>
              <li>回应您的评论和留言</li>
              <li>保护网站安全，防范恶意访问</li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            Cookie 使用
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>本网站使用 Cookie 技术来：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>记住您的偏好设置（如主题明暗模式）</li>
              <li>分析网站流量，提升用户体验</li>
              <li>确保网站安全运行</li>
            </ul>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
              您可以在浏览器中禁用 Cookie，但这可能影响部分功能。
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-violet-500" />
            信息保护
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>我们采取多种安全措施保护您的信息：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>使用 HTTPS 加密传输数据</li>
              <li>限制内部人员访问用户数据</li>
              <li>定期更新安全防护措施</li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-rose-500" />
            第三方链接
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>
              本网站可能包含指向第三方网站的链接。我们不对这些第三方网站的隐私政策负责。
              建议您在访问任何外部链接时查看其隐私政策。
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            政策更新
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>
              我们可能会不时更新本隐私政策。任何更改将在本页面上发布，更新日期将显示在页面顶部。
              建议您定期查看本页面以了解最新信息。
            </p>
          </div>
        </motion.section>

        {/* Contact */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl sm:rounded-[30px] shadow-[0_28px_70px_-44px_rgba(14,165,233,0.45)] p-6 sm:p-8 md:p-10"
        >
          <h2 className="text-xl font-bold text-white mb-3">联系我们</h2>
          <p className="text-white/80 leading-7 mb-4">
            如果您对本隐私政策有任何疑问，请通过以下方式联系我们：
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-5 py-2.5 text-white font-medium hover:bg-white/25 transition-colors"
          >
            前往联系页面
            <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.section>

      </div>
    </div>
  );
};

export default Privacy;
